import type { CSSProperties } from 'react'
import { colors, font, fontMono, radius } from 'ui'

export const STYLE_GUIDE_TITLE = 'Sentinel dashboard style guide'
export const STYLE_GUIDE_SLUG = 'sentinel-dashboard-style-guide'
export const STYLE_GUIDE_VERSION = '1.0'
export const STYLE_GUIDE_IMPORT_TS = "import { Button } from 'ui'"
export const STYLE_GUIDE_IMPORT_SCSS = "@use '../ui/styles' as *;"

export const USAGE_RULES: Array<[string, string]> = [
    ['Accent', 'One primary action per view. Everything else is neutral. Never use the accent for decoration.'],
    ['Colour', 'No hex values in page modules. @use the ui/styles tokens; add a token if one is missing.'],
    ['Surfaces', 'Pages sit on surface2. Cards are white with a hairline border and no shadow. Shadows are for overlays only.'],
    ['Accent edges', 'A surface with a coloured edge bar (Card tone, accentTop, CollapsibleSection group) is square-cornered. Plain cards keep the radius.'],
    ['Status', 'Pill tones are semantic: good / bad / warn / info / accent. Grey is the default for anything neutral.'],
    ['Tables', 'Micro-label headers, 13px body, hairline rows, hover highlight. Pair with Toolbar above and Pagination below.'],
    ['Forms', 'Every control lives in a Field with a label. Use FieldGrid for multi-column layouts. Errors replace hints.'],
    ['Headers', 'PageHeader for the page title, Section for groups, Card title for panels. Do not add extra heading styles.'],
    ['Collapsing', 'Use CollapsibleSection for optional or heavy content; default closed unless the page is about that content.'],
    ['Dialogs', 'Modal for confirmations and small forms; Drawer when the list behind should stay visible.'],
    ['Feedback', 'Banner for inline messages, EmptyState for empty/error panels, Notiflix (themed) for blocking confirms.'],
]

export const SWATCHES: { group: string; items: Array<[string, string]> }[] = [
    {
        group: 'Accent',
        items: [
            ['accent', colors.accent],
            ['accentDark', colors.accentDark],
            ['accentSoft', colors.accentSoft],
            ['accentTint', colors.accentTint],
        ],
    },
    {
        group: 'Text',
        items: [
            ['ink', colors.ink],
            ['ink2', colors.ink2],
            ['ink3', colors.ink3],
            ['ink4', colors.ink4],
        ],
    },
    {
        group: 'Lines and surfaces',
        items: [
            ['line', colors.line],
            ['lineStrong', colors.lineStrong],
            ['lineSoft', colors.lineSoft],
            ['surface', colors.surface],
            ['surface2', colors.surface2],
            ['surface3', colors.surface3],
        ],
    },
    {
        group: 'Semantic',
        items: [
            ['good', colors.good],
            ['goodTint', colors.goodTint],
            ['bad', colors.bad],
            ['badTint', colors.badTint],
            ['warn', colors.warn],
            ['warnTint', colors.warnTint],
            ['info', colors.info],
            ['infoTint', colors.infoTint],
        ],
    },
]

export const TYPE_SCALE: Array<{ label: string; meta: string; sample: string; style: CSSProperties }> = [
    { label: 'Page title', meta: '22px / 600', sample: 'View cases', style: { fontSize: 22, fontWeight: 600 } },
    { label: 'Section title', meta: '16px / 600', sample: 'Funding pipeline', style: { fontSize: 16, fontWeight: 600 } },
    { label: 'Card title', meta: '14px / 600', sample: 'Clients by lender', style: { fontSize: 14, fontWeight: 600 } },
    { label: 'Body', meta: '13.5px / 400', sample: 'The quick brown fox jumps over the lazy dog.', style: { fontSize: 13.5 } },
    { label: 'Table / control', meta: '13px / 400', sample: 'amelia.hart@example.com', style: { fontSize: 13 } },
    { label: 'Secondary', meta: '12px / 400 ink3', sample: 'Last refreshed 2 minutes ago', style: { fontSize: 12, color: colors.ink3 } },
    {
        label: 'Micro label',
        meta: '11px / 600 upper',
        sample: 'Signed clients',
        style: { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.ink3 },
    },
    {
        label: 'Stat value',
        meta: '26px / 600 tabular',
        sample: '12,480',
        style: { fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
    },
]

export const SPACING_STEPS = [4, 8, 12, 16, 20, 24, 32] as const

export const SHAPE = {
    font,
    fontMono,
    cardRadius: radius.md,
    controlRadius: radius.sm,
    pillRadius: 999,
    controlHeight: 40,
    controlHeightSm: 28,
    borderWidth: 1,
    pageMax: 1280,
    sidebarWidth: 280,
}

export const COMPONENTS: Array<{ group: string; names: string[] }> = [
    {
        group: 'Layout',
        names: ['Page', 'PageHeader', 'Section', 'Card', 'CardGrid', 'Divider', 'CollapsibleSection', 'Modal', 'Drawer'],
    },
    {
        group: 'Data',
        names: [
            'StatCard',
            'StatGrid',
            'DataTable',
            'StackCell',
            'Pagination',
            'Toolbar',
            'ResultLine',
            'StatusPill',
            'CaseStatePill',
            'KeyValueList',
            'Banner',
            'EmptyState',
            'Spinner',
            'LoadingOverlay',
            'CodeBlock',
            'JsonViewer',
            'ProgressBar',
            'Steps',
            'FileTile',
        ],
    },
    {
        group: 'Controls',
        names: [
            'Button',
            'IconButton',
            'ButtonGroup',
            'SegmentedControl',
            'Tabs',
            'Field',
            'FieldGrid',
            'Input',
            'SearchInput',
            'Textarea',
            'NativeSelect',
            'ReactSelect',
            'DatePicker',
            'Checkbox',
            'Switch',
            'FileInput',
        ],
    },
    {
        group: 'Misc',
        names: ['Avatar', 'Menu', 'MenuItem', 'MenuLabel', 'ToastProvider', 'useToast'],
    },
]

export const AGENT_INSTRUCTIONS = [
    'You are implementing UI in sentinel-dashboard. Compose pages from `src/ui`; do not invent parallel components or page-local hex colours.',
    'Import React components from `ui`. In SCSS modules, `@use` `src/ui/styles` and consume tokens (`$accent`, `$ink`, `$radius`, `$sp-*`). If a token is missing, add it to both `_tokens.scss` and `tokens.ts`.',
    'One primary Button per view. Neutral for secondary actions, danger for destructive. Every form control sits in a Field with a label.',
    'Pages use Page + PageHeader + Section + Card. Tables use Toolbar, DataTable, Pagination. Status uses StatusPill tones. Overlays: Modal (blocking) or Drawer (keep the list visible).',
    'Do not add card shadows, gradients, or decorative accent. Accent edges (Card tone / accentTop, CollapsibleSection group) are square-cornered.',
]
